import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatCurrency';

const CourseCard = ({ title, image, course }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const intendedCoursePath = `/courses/${course.id}`;

  const handleCardClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSignInToEnroll = () => {
    setIsModalOpen(false);

    if (currentUser?.role === 'student') {
      navigate(intendedCoursePath);
      return;
    }

    navigate('/login', {
      state: {
        from: { pathname: intendedCoursePath },
        courseName: course.name,
      },
    });
  };

  const handleCreateAccount = () => {
    setIsModalOpen(false);
    navigate('/signup', {
      state: {
        from: { pathname: intendedCoursePath },
        courseName: course.name,
      },
    });
  };

  return (
    <>
      <div
        onClick={handleCardClick}
        className="group h-full cursor-pointer overflow-hidden rounded-lg bg-white shadow-lg transition-shadow hover:shadow-xl"
      >
        <div className="relative aspect-square">
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/45 to-transparent" />
          <div className="absolute bottom-4 right-4 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-gray-900 shadow-md transition-colors group-hover:bg-primary-600 group-hover:text-white">
            More info
          </div>
        </div>
      </div>

      {/* Course Details Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={image}
                alt={title}
                className="h-64 w-full object-cover sm:h-72 md:h-80"
              />
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">{course.name}</h2>

              {/* Course Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-lg mb-3">
                    Course Overview
                  </h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Duration:</span>
                      {course.duration.isOneDay
                        ? 'One Day Workshop'
                        : `${course.duration.months} months`}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Level:</span>
                      {course.academicLevel}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Type:</span>
                      {course.type}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Branch:</span>
                      {course.branch}
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Fee Structure</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Course Fee:</span>
                      {formatCurrency(course.fees.fullCourseFee)}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Registration:</span>
                      {formatCurrency(course.fees.registrationFee)}
                    </li>
                    <li className="flex items-center">
                      <span className="font-medium mr-2">Installments:</span>
                      {course.fees.numberOfInstallments} x{' '}
                      {formatCurrency(course.installmentAmount)}
                    </li>
                    <li className="flex items-center text-green-600">
                      <span className="font-medium mr-2">One-off Payment:</span>
                      {formatCurrency(course.oneoffPayment)} (
                      {course.fees.oneoffDiscount}% off)
                    </li>
                  </ul>
                </div>
              </div>

              {/* Curriculum Preview */}
              <div className="mb-8">
                <h3 className="font-semibold text-lg mb-3">
                  Curriculum Preview
                </h3>
                <div className="space-y-4">
                  {course.curriculum?.map((module, index) => (
                    <div
                      key={index}
                      className="border-l-2 border-primary-200 pl-4"
                    >
                      <h4 className="font-medium text-primary-600">
                        {module.title}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {module.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Next Batch */}
              {course.batches && course.batches.length > 0 && (
                <div className="bg-primary-50 p-4 rounded-lg mb-8">
                  <h3 className="font-semibold text-lg mb-2">Next Batch</h3>
                  <p className="text-gray-600">
                    Starting: {course.batches[0].startDate.date}/
                    {course.batches[0].startDate.month}/
                    {course.batches[0].startDate.year}
                  </p>
                  <p className="text-gray-600">
                    Limited to {course.batches[0].maxStudents} students
                  </p>
                </div>
              )}

              {/* Call to Action */}
              <div className="mb-4 rounded-lg bg-primary-50 p-4">
                <p className="text-sm font-medium text-primary-800">
                  Choose this course, then sign in or create your student account to continue with enrollment.
                </p>
              </div>

              <div className="flex flex-col gap-3 md:flex-row">
                <button
                  type="button"
                  onClick={handleSignInToEnroll}
                  className="flex-1 rounded-lg bg-primary-600 py-3 text-center text-white transition-colors hover:bg-primary-700"
                >
                  {currentUser?.role === 'student' ? 'Continue to course' : 'Sign in to enroll'}
                </button>
                {!currentUser ? (
                  <button
                    type="button"
                    onClick={handleCreateAccount}
                    className="flex-1 rounded-lg border-2 border-primary-600 py-3 text-center text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    Create account
                  </button>
                ) : null}
                <button
                  onClick={handleCloseModal}
                  className="flex-1 rounded-lg border-2 border-gray-300 py-3 text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CourseCard;
